'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import { Lock, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { useSessionContext } from '@/contexts/session-context';
import { useSidebar } from '@/contexts/sidebar-context';
import { useLanguage } from '@/contexts/language-context';
import { useNotifications } from '@/contexts/notification-context';
import type { Notification } from '@/types/notification';
import SettingsModal from './settings-modal';
import { useAuth } from '@/contexts/auth-context';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { normalizeUserId } from '@/lib/user-session-rules';
import { SessionAccessDialog } from '@/components/session-access-dialog';
import { FeatureAccessDialog } from '@/components/feature-access-dialog';
import { getFeatureRestrictionFromResponse, normalizeUserLevel, type FeatureRestriction } from '@/lib/feature-permissions';
import { useLoginDialog } from '@/components/login-dialog';
import { getRecommendationAccessRestriction } from '@/lib/recommendation-access';
import { getNotificationDate } from '@/lib/notification-date';
import { useNewChat } from '@/hooks/use-new-chat';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Sidebar() {
  const { sessions, setSessions, setSessionStatus, beginSessionNavigation } = useSessionContext();
  const { isOpen, isMobile, closeSidebar } = useSidebar();
  const { t, isRTL, language } = useLanguage();
  const { notifications, setNotifications, setActiveNotification, markAsRead, unreadCount, loaded: notificationsLoaded } = useNotifications();
  const [hasMounted, setHasMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPastChatsOpen, setIsPastChatsOpen] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [recommendationDialog, setRecommendationDialog] = useState<{ open: boolean; restriction: FeatureRestriction }>({ open: false, restriction: 'login' });
  const router = useRouter();
  const pathname = usePathname();
  const { user, setUser, loading: authLoading, isLogin } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const userLevel = user?.level ?? 'L0';
  const accountLevel = normalizeUserLevel(user?.level);
  const newChat = useNewChat();
  const isSessionLocked = newChat.isLocked;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Fetch sessions once on mount (frontend caching - no refetch on "New Chat")
  useEffect(() => {
    if (authLoading) return;

    logger.info('[Sidebar] Calling fetchSessions()...');
    if (user?.userId) {
      fetchSessions(user.userId);
      fetchNotifications();
      return;
    }

    setSessions([]);
    setSessionStatus('ready');
    setNotifications([]);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.userId]);

  function showRecommendationPrompt(restriction: FeatureRestriction) {
    setRecommendationDialog({ open: true, restriction });
  }

  function closeMobileSidebar() {
    if (isMobile) {
      closeSidebar();
    }
  }

  function getNotificationRestrictionTitle(restriction: FeatureRestriction | null): string | undefined {
    if (!restriction) return undefined;
    return restriction === 'login' ? 'Sign in to view insight' : 'Upgrade to view advanced insight';
  }

  async function fetchSessions(userId: string) {
    setLoading(true);
    setSessionStatus('loading');
    try {
      const res = await fetch(`/api/sessions?userId=${encodeURIComponent(String(userId))}`);

      if (!res.ok) {
        // Handle authentication errors by redirecting to login
        if (res.status === 401) {
          logger.warn('[Sidebar] Authentication failed - redirecting to login');
          // window.location.href = '/api/auth/login';
          const data = await res.json();
          toast.error(data.error, { position: 'top-center' });
          setUser(null);
          return;
        }
        throw new Error(`Failed to fetch sessions: ${res.status}`);
      }

      const data = await res.json();
      logger.info('[Sidebar] Fetched sessions from API:', data.sessions?.length || 0);
      setSessions(data.sessions || []);
      setSessionStatus('ready');
      logger.info('[Sidebar] Context updated with sessions');
    } catch (error) {
      logger.error('Failed to fetch sessions:', error);
      setSessions([]);
      setSessionStatus('error');
    } finally {
      setLoading(false);
    }
  }

  async function fetchNotifications() {
    try {
      const normalizedUserId = normalizeUserId(user?.userId);
      const query = normalizedUserId ? `?userId=${encodeURIComponent(String(normalizedUserId))}` : '';
      const res = await fetch(`/api/notifications${query}`);

      if (!res.ok) {
        if (res.status === 401) {
          // Session auth handled by fetchSessions redirect — skip silently
          return;
        }
        throw new Error(`Failed to fetch notifications: ${res.status}`);
      }

      const data = await res.json();
      logger.info('[Sidebar] Fetched notifications:', data.notifications?.length || 0);
      setNotifications(data.notifications || []);
    } catch (error) {
      logger.error('Failed to fetch notifications:', error);
      setNotifications([]);
    }
  }

  async function handleNotificationClick(notification: Notification) {
    if (authLoading) return;

    const recommendationRestriction = getNotificationRestriction(notification);

    if (recommendationRestriction) {
      showRecommendationPrompt(recommendationRestriction);
      return;
    }

    // If notification already has a session, navigate directly
    if (notification.session_id) {
      if (!notification.read) {
        markAsRead(notification.notification_id);
      }
      router.push(`/chat/${notification.session_id}`);
      closeMobileSidebar();
      return;
    }

    // First-open: fetch detail by date, then navigate
    try {
      const routeKey = getNotificationRouteKey(notification);
      const encodedDate = encodeURIComponent(routeKey);
      const normalizedUserId = normalizeUserId(user?.userId);
      const params = new URLSearchParams();
      if (normalizedUserId) {
        params.set('userId', normalizedUserId);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`/api/notifications/${encodedDate}${query}`);

      if (!res.ok) {
        const restriction = await getFeatureRestrictionFromResponse(res);
        if (restriction) {
          showRecommendationPrompt(restriction);
          return;
        }

        throw new Error(`Failed to fetch notification detail: ${res.status}`);
      }

      const data = await res.json();
      markAsRead(notification.notification_id);

      // First-open: store content in context and navigate with notification param
      if (data.content) {
        setActiveNotification({
          notificationId: routeKey,
          content: data.content,
          title: data.title || notification.title,
          type: data.type || notification.type,
        });
      }

      router.push(`/?notification=${encodeURIComponent(routeKey)}`);
      closeMobileSidebar();
    } catch (error) {
      logger.error('Failed to handle notification click:', error);
      toast.error('Failed to load daily report', { position: 'top-center' });
    }
  }

  function handleNewChat() {
    newChat.startNewChat(closeMobileSidebar);
  }

  function handleAccountClick() {
    if (!isLogin) {
      openLoginDialog();
      closeMobileSidebar();
      return;
    }

    setIsSettingsOpen(true);
  }

  function handleSessionClick(sessionId: string) {
    if (currentSessionId === sessionId) {
      closeMobileSidebar();
      return;
    }

    beginSessionNavigation(sessionId);
    router.push(`/chat/${sessionId}`);
    closeMobileSidebar();
  }

  // Extract current session ID from pathname
  const currentSessionId = pathname.match(/\/chat\/([^\/]+)/)?.[1];

  function getNotificationRestriction(notification: Notification): FeatureRestriction | null {
    return getRecommendationAccessRestriction({
      level: userLevel,
      isLoggedIn: isLogin,
      notificationDate: getNotificationDate(notification),
    });
  }

  function getNotificationRouteKey(notification: Notification): string {
    return getNotificationDate(notification) || notification.notification_id;
  }

  if (!hasMounted) {
    return null;
  }

  function startRenamingSession(sessionId: string) {
    if (isSessionLocked) return;

    const session = sessions.find((s) => s.session_id === sessionId);
    setRenamingSessionId(sessionId);
    setRenameValue(session?.session_title || '');
  }

  function cancelRenamingSession() {
    setRenamingSessionId(null);
    setRenameValue('');
  }

  async function commitRenamingSession() {
    if (isSessionLocked) return;

    if (!renamingSessionId) return;
    const nextTitle = renameValue.trim();
    if (!nextTitle) {
      cancelRenamingSession();
      return;
    }

    // Call backend to rename session
    const dto = {
      sessionId: renamingSessionId,
      userId: user?.userId,
      session_title: nextTitle,
    };

    try {
      // Send PATCH request to backend
      const renameResponse = await fetch('/api/sessions', {
        method: 'PATCH',
        body: JSON.stringify(dto),
      });

      if (!renameResponse.ok) {
        toast.error('Failed to rename session', { position: 'top-center' });
        return;
      } else {
        const reSessionInfo = await renameResponse.json();
        if (!reSessionInfo?.session_title) {
          throw new Error('V Agent API error');
        }
        setSessions(sessions.map((s) => (s.session_id === renamingSessionId ? { ...s, session_title: reSessionInfo.session_title } : s)));
        cancelRenamingSession();
        toast.success('Session renamed successfully', { position: 'top-center' });
      }
    } catch (error) {
      logger.error('Failed to rename session:', error);
      toast.error('Failed to rename session', { position: 'top-center' });
      return;
    }
  }

  function requestDeleteSession(sessionId: string) {
    if (isSessionLocked) return;

    setDeleteSessionId(sessionId);
    setDeleteDialogOpen(true);
  }

  function confirmDeleteSession() {
    if (isSessionLocked) return;

    if (!deleteSessionId) return;

    async function deleteSession(sessionId: string) {
      try {
        const dto = {
          session_id: sessionId,
          user_id: user?.userId,
        };
        const deleteSessionResponse = await fetch('/api/sessions', {
          method: 'POST',
          body: JSON.stringify(dto),
        });
        if (!deleteSessionResponse.ok) {
          toast.error('Failed to delete session', { position: 'top-center' });
          return;
        } else {
          toast.success('Session deleted successfully', { position: 'top-center' });
          setSessions(sessions.filter((s) => s.session_id !== sessionId));
          router.push(`/`);
        }
      } catch (error) {
        logger.error('Failed to delete session:', error);
        toast.error('Failed to delete session', { position: 'top-center' });
        setDeleteDialogOpen(false);
        setDeleteSessionId(null);
        return;
      } finally {
        setDeleteDialogOpen(false);
        setDeleteSessionId(null);
      }
    }

    deleteSession(deleteSessionId);
  }

  // Calculate position class based on RTL and open state.
  // The same transform-based drawer works with the mobile and desktop widths.
  const getPositionClass = () => {
    if (isRTL) {
      return `right-0 border-l ${isOpen ? 'translate-x-0' : 'translate-x-full'}`;
    }
    return `left-0 border-r ${isOpen ? 'translate-x-0' : '-translate-x-full'}`;
  };

  const cancelLabel = language === 'zh' ? '取消' : 'Cancel';
  const isNewChatDisabled = newChat.isDisabled;

  return (
    <>
      {isMobile && isOpen && (
        <button
          type="button"
          aria-label={t('common.hideSidebar')}
          className="fixed inset-0 z-40 bg-black/55 backdrop-blur-[1px] md:hidden"
          onClick={closeSidebar}
        />
      )}
      <aside
        style={{ top: 0, bottom: 0, paddingTop: 64 }}
        className={`fixed w-[calc(100vw-48px)] max-w-80 bg-[#231f20] border-white/[0.06] shadow-[0_4px_4px_rgba(0,0,0,0.25)] flex flex-col z-50 transition-transform duration-300 md:z-40 md:w-[280px] md:max-w-none ${getPositionClass()}`}>
        {/* Sticky Header */}
        <div className="px-4 pt-2 flex-shrink-0">
          {/* New Chat Button */}
          <button
            onClick={handleNewChat}
            disabled={isNewChatDisabled}
            title={newChat.buttonTitle}
            className={`w-full h-9 rounded-lg bg-white/[0.04] px-3 py-2 transition-colors flex items-center gap-[9px] ${
              isNewChatDisabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-white/[0.08] cursor-pointer'
            }`}>
            <Image
              src="/images/sidebar/new-chat.svg"
              alt=""
              width={20}
              height={20}
              className="size-5 shrink-0"
              aria-hidden="true"
            />
            <span className={`text-xs font-semibold leading-[14px] tracking-[0.02em] ${isNewChatDisabled ? 'text-white/45' : 'text-white'}`}>{t('sidebar.newChat')}</span>
          </button>
        </div>

        {/* Insights For You Section */}
        <div className="mt-4 flex-shrink-0">
          <div className="px-4">
            <div
              id="sidebar-insights-title"
              data-testid="insights-heading"
              aria-label={t('sidebar.insightsForYou')}
              className="flex h-4 w-full items-center gap-1 text-xs font-normal leading-[14px] tracking-[-0.01em] text-[#666666]">
              <Image
                src="/images/sidebar/insight.svg"
                alt=""
                width={16}
                height={16}
                className="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{t('sidebar.insightsForYou')}</span>
              {unreadCount > 0 && (
                <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-[#EE531B] text-[9px] font-medium leading-none text-white">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>

          <div
            aria-labelledby="sidebar-insights-title"
            data-testid="insights-scroll"
            className="sidebar-scrollbar mt-2 h-[190px] overflow-y-auto">
            <div className="ml-4 w-[calc(100%-32px)] space-y-2 md:w-[248px]">
              {!notificationsLoaded ? (
                <div className="text-white/30 text-sm text-center py-4">{t('sidebar.loading')}</div>
              ) : notifications.length === 0 ? (
                <div className="py-4 text-center text-xs text-white/30">{t('sidebar.noNotifications')}</div>
              ) : (
                notifications.map((notification) => {
                  const restriction = getNotificationRestriction(notification);

                  return (
                    <button
                      key={notification.notification_id}
                      data-testid="insight-card"
                      onClick={() => handleNotificationClick(notification)}
                      aria-disabled={!!restriction}
                      title={getNotificationRestrictionTitle(restriction)}
                      className="min-h-[58px] w-full cursor-pointer rounded-xl bg-white/[0.04] p-3 text-left transition-colors hover:bg-white/[0.08]">
                      <div className="flex items-start gap-1">
                        <Image
                          src="/images/sidebar/insight-item.svg"
                          alt=""
                          width={16}
                          height={16}
                          className="size-4 shrink-0"
                          aria-hidden="true"
                        />
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <div className="flex h-[18px] items-center justify-between gap-1">
                            <div className="truncate text-xs font-semibold leading-[14px] tracking-[0.02em] text-white">{notification.title}</div>
                            {restriction && (
                              <Image
                                src="/images/sidebar/insight-action.svg"
                                alt=""
                                width={14}
                                height={14}
                                className="size-3.5 shrink-0"
                                aria-hidden="true"
                              />
                            )}
                          </div>
                          <div className="truncate text-xs font-normal leading-[14px] tracking-[-0.01em] text-[#666666]">{notification.preview}</div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Recent Chats Section */}
        <div className="mt-4 flex flex-1 flex-col overflow-hidden">
          {/* Recent Chats Header - Sticky */}
          <div className="flex-shrink-0 px-4">
            <button
              onClick={() => setIsPastChatsOpen(!isPastChatsOpen)}
              className="flex h-4 w-full cursor-pointer items-center gap-1 text-xs font-normal leading-[14px] tracking-[-0.01em] text-[#666666] transition-colors hover:text-[#999999]">
              <Image
                src="/images/sidebar/recent.svg"
                alt=""
                width={16}
                height={16}
                className="size-4 shrink-0"
                aria-hidden="true"
              />
              <span className="flex-1 text-left">{t('sidebar.recentChats')}</span>
              <Image
                src="/images/sidebar/chevron-up.svg"
                alt=""
                width={14}
                height={14}
                className={`size-3.5 shrink-0 transition-transform duration-200 ${isPastChatsOpen ? '' : 'rotate-180'}`}
                aria-hidden="true"
              />
            </button>
          </div>

          {/* Scrollable Session History */}
          {isPastChatsOpen && (
            <div
              aria-label={t('sidebar.recentChats')}
              className="sidebar-scrollbar mt-2 flex-1 overflow-y-auto pb-4">
              <div className="ml-4 w-[calc(100%-32px)] space-y-1 md:w-[248px]">
                {loading ? (
                  <div className="py-4 text-center text-xs text-white/30">{t('sidebar.loading')}</div>
                ) : sessions.length === 0 ? (
                  <div className="py-4 text-center text-xs text-white/30">{t('sidebar.noSessions')}</div>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="relative group">
                      {renamingSessionId === session.session_id ? (
                        <div className={`w-full rounded-[9px] border border-white/[0.10] bg-white/[0.04] px-3 py-2.5 text-left ${isRTL ? 'pl-10' : 'pr-10'}`}>
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitRenamingSession();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelRenamingSession();
                              }
                            }}
                            onBlur={() => commitRenamingSession()}
                            className="w-full bg-transparent border border-white/[0.12] rounded-md px-2 py-1 text-xs font-medium text-[#ccc] outline-none focus:border-white/[0.20]"
                            placeholder={t('sidebar.newConversation')}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onMouseDown={() => {
                            if (renamingSessionId && renamingSessionId !== session.session_id) {
                              cancelRenamingSession();
                            }
                          }}
                          onClick={() => handleSessionClick(session.session_id)}
                          className={`w-full cursor-pointer rounded-[9px] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.08] ${
                            currentSessionId === session.session_id ? 'bg-white/[0.04]' : ''
                          } ${isRTL ? 'pl-10' : 'pr-10'}`}>
                          <div className="truncate text-xs font-normal leading-[14px] tracking-[-0.01em] text-white">{session.session_title || t('sidebar.newConversation')}</div>
                        </button>
                      )}

                      <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-2' : 'right-2'}`}>
                        {isSessionLocked ? (
                          <button
                            type="button"
                            disabled
                            title={newChat.lockedTitle}
                            aria-label={newChat.lockedTitle}
                            className="w-8 h-8 rounded-[10px] opacity-0 group-hover:opacity-100 transition-opacity bg-white/0 flex items-center justify-center cursor-not-allowed">
                            <Lock className="w-4 h-4 text-white/35" />
                          </button>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label="More"
                                onMouseDown={() => {
                                  if (renamingSessionId && renamingSessionId !== session.session_id) {
                                    cancelRenamingSession();
                                  }
                                }}
                                className="w-8 h-8 rounded-[10px] opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity bg-white/0 hover:bg-white/[0.08] flex items-center justify-center cursor-pointer">
                                <MoreHorizontal className="w-4 h-4 text-white/60" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              sideOffset={6}
                              align={isRTL ? 'start' : 'end'}
                              className="min-w-[140px] rounded-[12px] border border-white/[0.08] bg-[#231f20] text-white/80 shadow-lg">
                              <DropdownMenuItem
                                onSelect={() => startRenamingSession(session.session_id)}
                                className="cursor-pointer hover:bg-white/[0.06] focus:bg-white/[0.06] focus:text-white">
                                <Pencil className="w-4 h-4 text-white/50" />
                                <span>{t('sidebar.rename')}</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() => requestDeleteSession(session.session_id)}
                                className="cursor-pointer hover:bg-white/[0.06] focus:bg-white/[0.06] focus:text-[#ff8a80]">
                                <Trash2 className="w-4 h-4 text-[#ff8a80]" />
                                <span>{t('sidebar.delete')}</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-white/[0.06] p-4">
          <div className="flex w-full items-center gap-3">
            <button
              type="button"
              onClick={handleAccountClick}
              aria-label={isLogin ? 'Open settings' : 'Log in'}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-left">
              <img
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#F98715] text-sm font-semibold text-white"
                src={((user as unknown as { avatar?: string | null })?.avatar ?? '') || '/AI-logoImage.png'}
                alt="AICoach"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex h-[18px] min-w-0 items-center gap-1">
                  <span className={`min-w-0 truncate text-xs font-semibold leading-[14px] tracking-[0.02em] ${isLogin ? 'text-white' : 'text-[#F98715]'}`}>
                    {user?.email || t('sidebar.notLoggedIn')}
                  </span>
                  {isLogin && (
                    <span
                      data-testid="account-level-badge"
                      className="inline-flex h-4 min-w-[31px] shrink-0 items-center justify-center rounded-full border border-[#FA8614]/[0.44] bg-[#FA8714]/20 px-1 text-[9px] font-medium leading-none text-[#F98715]">
                      {accountLevel}
                    </span>
                  )}
                </div>
                <div className="truncate text-[10px] font-normal leading-3 tracking-[0.02em] text-[#666666]">Ask Before You Trade</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setIsSettingsOpen(true)}
              aria-label="Settings"
              className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-white/[0.04]">
              <Image
                src="/images/sidebar/settings.svg"
                alt=""
                width={28}
                height={28}
                className="size-7"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </aside>
      {/* Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      <SessionAccessDialog
        open={newChat.limitDialog.open}
        onOpenChange={newChat.setLimitDialogOpen}
        variant="limit"
        language={language}
        level={userLevel}
        limit={newChat.limitDialog.limit}
      />

      <FeatureAccessDialog
        open={recommendationDialog.open}
        onOpenChange={(open) => setRecommendationDialog((prev) => ({ ...prev, open }))}
        restriction={recommendationDialog.restriction}
        feature="recommendation"
      />

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeleteSessionId(null);
        }}>
        <AlertDialogContent
          size="sm"
          className="bg-[#231f20] border border-white/[0.08]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('sidebar.delete')}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">{t('sidebar.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              variant="outline"
              className="border-white/[0.16] bg-white/[0.06] text-white hover:bg-white/[0.06] hover:text-white">
              {cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              className="bg-[#ff4d4f] text-white hover:bg-[#ff4d4f]/90 hover:text-white"
              onClick={confirmDeleteSession}>
              {t('sidebar.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
